import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@zaogig/auth';
import { Toaster } from 'sonner';
import { type ReactNode, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import LoginPage from '../pages/LoginPage';
import OAuthCallback from '../pages/OAuthCallback';
import LandingPage from '../pages/LandingPage';
import HomePage from '../pages/HomePage';
import { JobListPage } from '../pages/JobListPage';
import { JobDetailPage } from '../pages/JobDetailPage';
import { BottomNav } from '../components/BottomNav';
import AttendancePage from '../pages/AttendancePage';
import InterviewPage from '../pages/InterviewPage';
import ProfilePage from '../pages/ProfilePage';

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {children}
      <BottomNav />
    </div>
  );
}

const queryClient = new QueryClient();

function AppContent() {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => navigate('/login', { replace: true });
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, [navigate]);

  return (
    <>
      <Toaster position="top-center" richColors />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/oauth/wechat/callback" element={<OAuthCallback />} />

        {/* Protected app routes (login required) */}
        <Route path="/app" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/home" element={<ProtectedRoute><AppLayout><HomePage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/jobs" element={<ProtectedRoute><AppLayout><JobListPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/jobs/:id" element={<ProtectedRoute><AppLayout><JobDetailPage /></AppLayout></ProtectedRoute>} />
        <Route path="/app/interview" element={<ProtectedRoute><AppLayout><InterviewPage interviews={[]} jobs={[]} /></AppLayout></ProtectedRoute>} />
        <Route path="/app/attendance" element={<ProtectedRoute><AppLayout><AttendancePage onComplete={() => {}} onBack={() => window.history.back()} /></AppLayout></ProtectedRoute>} />
        <Route path="/app/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export function App() {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider baseUrl={baseUrl}>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
